/**
 * Access-token refresh, shared by the api client's 401 interceptor and
 * background keep-alives (see ensureFreshAccessToken).
 */
import axios from 'axios';
import { STORAGE_KEYS, API_PATHS } from '@utils/constants';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

/**
 * Attempt to refresh the access token
 */
async function refreshAccessToken(): Promise<string> {
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  // Make direct axios call to avoid interceptor loop
  const response = await axios.post<{ access_token: string; refresh_token?: string }>(
    `${API_URL}${API_PATHS.AUTH}/refresh`,
    { refresh_token: refreshToken },
    { headers: { 'Content-Type': 'application/json' } }
  );

  const { access_token, refresh_token: newRefreshToken } = response.data;

  // Store new tokens
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access_token);
  if (newRefreshToken) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
  }

  return access_token;
}

// One refresh at a time, shared by the 401 interceptor and
// ensureFreshAccessToken(): the IdP may rotate refresh tokens, so spending the
// same refresh token twice concurrently can revoke the session outright.
let inFlightRefresh: Promise<string> | null = null;

export function refreshOnce(): Promise<string> {
  inFlightRefresh ??= refreshAccessToken().finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

/** The JWT's exp/iat (seconds), or null if it can't be decoded. Unverified: scheduling only. */
function timeClaims(token: string): { exp: number; iat: number | null } | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const { exp, iat } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      exp?: unknown;
      iat?: unknown;
    };
    if (typeof exp !== 'number') return null;
    return { exp, iat: typeof iat === 'number' ? iat : null };
  } catch {
    return null;
  }
}

// Renew by AGE, not by time remaining: what actually lapses is the IdP
// session behind the refresh token (Keycloak agenticcluster: SSO Session Idle
// 30 min), which only a refresh keeps alive. Waiting until a 1h token is near
// expiry (~50 min in) lets that session idle out first. 15 min stays well
// inside a 30 min idle whatever the Sanctum TTL.
const MAX_TOKEN_AGE_SECONDS = 15 * 60;
const MIN_REMAINING_SECONDS = 2 * 60;
// Tokens without iat: fall back to time remaining.
const FALLBACK_MIN_VALIDITY_SECONDS = 10 * 60;

/**
 * Return the stored access token, refreshing it first when it is older than
 * `maxAgeSeconds` (or about to expire).
 *
 * For background keep-alives (the workspace page's ws_auth cookie loop). A
 * user working inside the terminal/IDE iframe makes no API calls, so the 401
 * interceptor never fires. Without this, the stored token and the IdP session
 * behind the refresh token lapse while the user is active, and the next
 * foreground call bounces them to /login. Never redirects or clears tokens on
 * failure: it returns the current token and leaves logout to the foreground
 * flow.
 */
export async function ensureFreshAccessToken(
  maxAgeSeconds = MAX_TOKEN_AGE_SECONDS,
): Promise<string | null> {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  if (!token) return null;
  const claims = timeClaims(token);
  if (!claims) return token;
  const now = Date.now() / 1000;
  const remaining = claims.exp - now;
  const due = claims.iat === null
    ? remaining <= FALLBACK_MIN_VALIDITY_SECONDS
    : now - claims.iat >= maxAgeSeconds || remaining <= MIN_REMAINING_SECONDS;
  if (!due || !localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)) return token;
  try {
    return await refreshOnce();
  } catch {
    return token;
  }
}
